// Service to work with the existing gtag implementation in index.html
import type { Product, CartItem } from '../types/Product';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

class GtagService {
  private initialized = false;
  // private measurementId = 'G-NRJZH1DVZ6'; // From index.html

  initialize(): void {
    // Check if gtag is already loaded from index.html
    if (typeof window.gtag === 'function') {
      this.initialized = true;
      console.log('Using existing gtag from index.html');
    } else {
      console.warn('gtag not found - Google Analytics may not be loaded');
    }
  }

  private sendEvent(eventName: string, parameters?: any): void {
    if (!this.initialized || typeof window.gtag !== 'function') {
      console.warn('Analytics not initialized, skipping event:', eventName);
      return;
    }

    console.log('Sending GA event:', eventName, parameters);
    window.gtag('event', eventName, parameters);
  }

  // User identification
  setUserId(userId: string): void {
    if (this.initialized && window.gtag) {
      window.gtag('set', { user_id: userId });
    }
  }

  setUserProperties(properties: any): void {
    if (this.initialized && window.gtag) {
      window.gtag('set', { user_properties: properties });
    }
  }

  // Page tracking
  trackPageView(path?: string, title?: string): void {
    if (!this.initialized) return;

    const pageData: any = {
      page_path: path || window.location.pathname,
      page_title: title || document.title,
    };

    this.sendEvent('page_view', pageData);
  }

  // E-commerce tracking
  trackProductView(product: Product): void {
    this.sendEvent('view_item', {
      currency: 'INR',
      value: product.price,
      items: [{
        item_id: product.sku || product.id,
        item_name: product.name,
        item_category: product.category,
        item_brand: product.brand,
        price: product.price,
        quantity: 1,
      }],
    });
  }

  trackAddToCart(product: Product, quantity: number = 1): void {
    this.sendEvent('add_to_cart', {
      currency: 'INR',
      value: product.price * quantity,
      items: [{
        item_id: product.sku || product.id,
        item_name: product.name,
        item_category: product.category,
        item_brand: product.brand,
        price: product.price,
        quantity: quantity,
      }],
    });
  }

  trackRemoveFromCart(product: Product, quantity: number = 1): void {
    this.sendEvent('remove_from_cart', {
      currency: 'INR',
      value: product.price * quantity,
      items: [{
        item_id: product.sku || product.id,
        item_name: product.name,
        item_category: product.category,
        item_brand: product.brand,
        price: product.price,
        quantity: quantity,
      }],
    });
  }

  trackViewCart(items: CartItem[], total: number): void {
    const cartItems = items.map(item => ({
      item_id: item.sku || item.id,
      item_name: item.name,
      item_category: item.category,
      item_brand: item.brand,
      price: item.price,
      quantity: item.quantity,
    }));

    this.sendEvent('view_cart', {
      currency: 'INR',
      value: total,
      items: cartItems,
    });
  }

  trackBeginCheckout(items: CartItem[], total: number): void {
    const checkoutItems = items.map(item => ({
      item_id: item.sku || item.id,
      item_name: item.name,
      item_category: item.category,
      item_brand: item.brand,
      price: item.price,
      quantity: item.quantity,
    }));

    this.sendEvent('begin_checkout', {
      currency: 'INR',
      value: total,
      items: checkoutItems,
    });
  }

  trackPurchase(orderData: {
    orderId: string;
    total: number;
    items: CartItem[];
    shipping?: number;
    tax?: number;
    coupon?: string;
  }): void {
    const purchaseItems = orderData.items.map(item => ({
      item_id: item.sku || item.id,
      item_name: item.name,
      item_category: item.category,
      item_brand: item.brand,
      price: item.price,
      quantity: item.quantity,
    }));

    this.sendEvent('purchase', {
      transaction_id: orderData.orderId,
      value: orderData.total,
      currency: 'INR',
      shipping: orderData.shipping || 0,
      tax: orderData.tax || 0,
      coupon: orderData.coupon,
      items: purchaseItems,
    });
  }

  // Search tracking
  trackSearch(searchTerm: string, resultsCount?: number): void {
    const eventData: any = { search_term: searchTerm };
    if (resultsCount !== undefined) {
      eventData.results_count = resultsCount;
    }

    this.sendEvent('search', eventData);
  }

  // User engagement tracking
  trackLogin(method: string = 'phone'): void {
    this.sendEvent('login', { method });
  }

  trackSignup(method: string = 'phone'): void {
    this.sendEvent('sign_up', { method });
  }

  trackShare(contentType: string, itemId?: string): void {
    this.sendEvent('share', {
      content_type: contentType,
      item_id: itemId,
    });
  }

  // Custom events
  trackEvent(eventName: string, parameters?: any): void {
    this.sendEvent(eventName, parameters);
  }

  // Store-specific events
  trackFlyingCartAnimation(): void {
    this.sendEvent('flying_cart_animation', {
      event_category: 'engagement',
    });
  }

  trackQuickBuyAccess(): void {
    this.sendEvent('quick_buy_access', {
      event_category: 'feature_usage',
    });
  }

  trackThemeChange(theme: string): void {
    this.sendEvent('theme_change', {
      event_category: 'preferences',
      theme_name: theme,
    });
  }

  trackPickupLocationSelect(locationName: string): void {
    this.sendEvent('pickup_location_select', {
      event_category: 'checkout',
      location_name: locationName,
    });
  }

  trackEnquirySubmit(enquiryData: {
    orderId: string;
    total: number;
    itemCount: number;
    hasPickupLocation: boolean;
  }): void {
    this.sendEvent('enquiry_submit', {
      event_category: 'conversion',
      order_id: enquiryData.orderId,
      value: enquiryData.total,
      item_count: enquiryData.itemCount,
      has_pickup_location: enquiryData.hasPickupLocation,
    });
  }

  trackFilterUsage(filterType: string, filterValue: string): void {
    this.sendEvent('filter_usage', {
      event_category: 'product_discovery',
      filter_type: filterType,
      filter_value: filterValue,
    });
  }

  trackSortUsage(sortType: string): void {
    this.sendEvent('sort_usage', {
      event_category: 'product_discovery',
      sort_type: sortType,
    });
  }

  // Error tracking
  trackError(error: Error, fatal: boolean = false): void {
    this.sendEvent('exception', {
      description: error.message,
      fatal,
    });
  }
}

export const gtagService = new GtagService();